import { BuilderContent, builder } from '@builder.io/react'
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'

import BlogArticleTemplate from '@/legacy/blog/BlogArticleTemplate'
import { getAppInstallBannerCookie } from '@/legacy/components/AppInstallBanner/helpers'
import { BUILDER_BLOG_MODEL, BUILDER_FOOTER_MODEL, initBuilder } from '@/legacy/config/builder'
import { getContentReviewByUrl } from '@/legacy/core/api/contentReview'
import { getDevice } from '@/legacy/core/utils/getDevice'
import { getProductFromUrl } from '@/legacy/core/utils/getProductFromUrl'
import type { NextPageWithLayout } from '@/legacy/models/NextPageWithLayout'
import BlogArticleCmsProvider from '@/legacy/providers/BlogArticleCmsProvider'
import { isPreviewing } from '@/legacy/server/core/builder/isPreviewing'
import { isNotFound } from '@/legacy/server/core/builder/pageNotFound'
import { setCacheControlPage } from '@/legacy/server/core/builder/setCacheControlPage'

import { getAllAdsContent } from '@/legacy/server/services/builder/ads-content'

import '@/legacy/blocks/blogRegister'
import '@/legacy/blocks/register'
import { getRelatedArticles } from '@/legacy/server/services/builder/article'
import { ECS_ENV } from 'main/config/env'

initBuilder()

export async function getServerSideProps({
  params,
  resolvedUrl,
  res,
  req,
}: GetServerSidePropsContext) {
  const isPreview = isPreviewing({ req })
  const product = getProductFromUrl(resolvedUrl)
  const urlPath = resolvedUrl.split('?').shift() || ''
  const slug = [...(params?.slug || [])].pop()

  const articleModel = BUILDER_BLOG_MODEL

  const appInstallBanner = getAppInstallBannerCookie(req)

  const device = getDevice(req.headers)

  const article = await builder
    .get(articleModel, {
      res,
      req,
      includeRefs: true,
      userAttributes: { urlPath },
      query: { 'data.slug': slug },
      cachebust: ECS_ENV !== 'prd',
    })
    .promise()

  const categoryName = article?.data.category?.value.name

  const [footer, adsTop, adsMiddle, adsBottom, contentReview, relatedArticles] = await Promise.all([
    builder
      .get(BUILDER_FOOTER_MODEL, {
        res,
        req,
        userAttributes: { urlPath },
      })
      .promise(),
    ...getAllAdsContent({ req, res, urlPath }),
    isPreview ? Promise.resolve(null) : getContentReviewByUrl({ url: urlPath }),
    getRelatedArticles({ req, res, product, categoryName, slug }),
  ])

  const props = {
    isPreview,
    product,
    model: articleModel,
    article: article || null,
    footer: footer || null,
    adsTop: adsTop || null,
    adsMiddle: adsMiddle || null,
    adsBottom: adsBottom || null,
    contentReview,
    relatedArticles: relatedArticles || null,
    appInstallBanner,
    device,
  }
  setCacheControlPage({ page: article, res, req })

  return {
    notFound: isNotFound({ page: article, req }),
    props: { ...props },
  }
}

export type ArticleSSRProps = InferGetServerSidePropsType<typeof getServerSideProps>

const BlogArticle: NextPageWithLayout<ArticleSSRProps> = ({ ...props }) => {
  const hasPage = props.article && !props.isPreview

  return (
    <BuilderContent
      model={props.model}
      options={{ includeRefs: true }}
      {...(hasPage && { content: props.article })}
    >
      {(data, loading, content) => {
        if (loading) {
          return null
        }

        const cmsData = { ...props, data, content }

        return (
          <BlogArticleCmsProvider data={cmsData}>
            <BlogArticleTemplate />
          </BlogArticleCmsProvider>
        )
      }}
    </BuilderContent>
  )
}

export default BlogArticle
